import { createUploadAbortError } from '../../ui/batch-document-upload/upload-utils';
import { RATE_LIMIT_RETRY_BUFFER_MS, RATE_LIMIT_WINDOW_MS } from './batch-upload.types';

export interface RateLimiterOptions {
    maxRequests: number;
    windowMs?: number;
    retryBufferMs?: number;
}

const throwIfAborted = (signal?: AbortSignal): void => {
    if (signal?.aborted) {
        throw createUploadAbortError();
    }
};

const delay = (ms: number, signal?: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        if (ms <= 0) {
            resolve();
            return;
        }

        if (signal?.aborted) {
            reject(createUploadAbortError());
            return;
        }

        const timeout = setTimeout(() => {
            cleanup();
            resolve();
        }, ms);

        const onAbort = (): void => {
            cleanup();
            reject(createUploadAbortError());
        };

        const cleanup = (): void => {
            clearTimeout(timeout);
            signal?.removeEventListener('abort', onAbort);
        };

        signal?.addEventListener('abort', onAbort, { once: true });
    });

export class RateLimiter {
    private readonly maxRequests: number;
    private readonly windowMs: number;
    private readonly retryBufferMs: number;
    private readonly timestamps: number[] = [];
    private tail: Promise<void> = Promise.resolve();
    private pausedUntil = 0;

    constructor(options: RateLimiterOptions) {
        this.maxRequests = options.maxRequests;
        this.windowMs = options.windowMs ?? RATE_LIMIT_WINDOW_MS;
        this.retryBufferMs = options.retryBufferMs ?? RATE_LIMIT_RETRY_BUFFER_MS;
    }

    public get activeCount(): number {
        this.prune();
        return this.timestamps.length;
    }

    public isGloballyPaused(): boolean {
        return Date.now() < this.pausedUntil;
    }

    public pauseUntil(timestamp: number): void {
        this.pausedUntil = Math.max(this.pausedUntil, timestamp);
    }

    public clearPause(): void {
        this.pausedUntil = 0;
    }

    public async waitUntilReady(signal?: AbortSignal, onWaiting?: (resumeAt: number) => void): Promise<void> {
        while (Date.now() < this.pausedUntil) {
            throwIfAborted(signal);
            const resumeAt = this.pausedUntil;
            onWaiting?.(resumeAt);
            await delay(resumeAt - Date.now(), signal);
        }
    }

    public async acquire(signal?: AbortSignal, onWaiting?: (resumeAt: number) => void): Promise<void> {
        return new Promise((resolve, reject) => {
            const run = async (): Promise<void> => {
                try {
                    await this.waitForSlot(signal, onWaiting);
                    resolve();
                } catch (error) {
                    reject(error);
                }
            };

            this.tail = this.tail.then(run, run);
        });
    }

    private async waitForSlot(signal?: AbortSignal, onWaiting?: (resumeAt: number) => void): Promise<void> {
        while (true) {
            throwIfAborted(signal);
            await this.waitUntilReady(signal, onWaiting);
            this.prune();

            if (this.timestamps.length < this.maxRequests) {
                this.timestamps.push(Date.now());
                return;
            }

            const oldestTimestamp = this.timestamps[0];
            if (oldestTimestamp === undefined) {
                continue;
            }

            const waitMs = Math.max(
                this.retryBufferMs,
                oldestTimestamp + this.windowMs - Date.now() + this.retryBufferMs,
            );

            onWaiting?.(Date.now() + waitMs);
            await delay(waitMs, signal);
        }
    }

    private prune(now = Date.now()): void {
        const cutoff = now - this.windowMs;

        while (this.timestamps.length > 0) {
            const oldest = this.timestamps[0];
            if (oldest === undefined || oldest > cutoff) {
                break;
            }

            this.timestamps.shift();
        }
    }
}
